import React, { useState } from 'react'

import InputField     from 'src/components/InputField'
import InvalidForm    from 'src/components/InvalidForm'
import MoneyField     from 'src/components/MoneyField'
import SandwichField  from 'src/components/SandwichField'
import Intro          from '../Intro'
import DataSharing    from '../DataSharing'
import Tribute        from '../Tribute'
import useValidation  from '../useValidation'
import useRecord      from '../fiat/useRecord'
import useBeneficiarySelect from '../useBeneficiarySelect'
import useModalLock   from 'src/widgets/useModalLock'

import csrfHeaders      from 'src/utils/csrfHeaders'
import prettyAmount     from 'src/utils/prettyAmount'
import formatUsdAmount  from 'src/utils/formatUsdAmount'
import { humanize }     from 'src/utils/inflections'

const App = ({
  partner_item, partner_items, goal_partner_item, partner, i18n, defaultShares,
  submitUrl, foundation, partner_is_pledge, data_sharing, tribute, setDonation,
  donationTags, embeddedOn, min_amount, commonInputProps, balanceLabel, order,
  plugins, disable_donation_split, donationsPath, token, template,
  ...props
}) => {
  const [ loading, setLoading ] = useState(false)
  // Lock the host modal while the wallet-balance donation submits;
  // `loading` is true from submit start until its finally.
  useModalLock(loading)
  const [ amount, setAmount ] = useState(prettyAmount(props.amount))
  const [ balance, setBalance ] = useState(props.balance)
  const [ shares, setShares ] = useState(defaultShares)

  const knownFields = ['amount']
  const fieldSet = { amount: { required: true, min: min_amount, max: balance } }
  const validation = useValidation({ i18n, fieldSet })

  // `base` carries a whole sentence from the server rather than a
  // complaint about a named field, so it renders bare. Left in the
  // humanized set below it came out as "Base This donation has already
  // gone through.", humanize('base') being just "Base".
  //
  // One sender reaches this panel today: Widgets::DonationsController
  // #refuse_finalized_order, on a saveDraft against an order that already
  // took the donor's money — a settled wallet gift sits in `pending`,
  // which is exactly that. The panel's own endpoint could carry base too:
  // WalletDonationsController#create passes @order.errors straight
  // through. It doesn't today only because the validation behind it adds
  // `amount` keys, so treat that as the current state rather than a
  // guarantee. The fiat panel renders errors.base the same way
  // (fiat/PaymentOptions.jsx).
  const { base: baseErrors, ...fieldErrors } = validation.errors
  const unknownErrors = Object.entries(fieldErrors)
    .filter(([ key, msgs ]) => !knownFields.includes(key) && msgs?.length)
    .flatMap(([ key, msgs ]) => msgs.map(msg => `${humanize(key)} ${msg}`))

  const { partnerItem, projectId, ...beneficiarySelect } = useBeneficiarySelect({
    i18n, commonInputProps, partner_item, partner_items, goal_partner_item,
    disable_donation_split,
  })

  const donation = { state: { amount }, shares, currency: { id: 'usd' } }
  const session = { collect_email: false }
  const record = useRecord({
    i18n, order, partner, partnerItem, projectId, session, tribute,
    donationTags, embeddedOn, donation, plugins, donationsPath,
    widget: { token, template },
  })

  const handleAmountChange = e => {
    setAmount(e.target.value)
    validation.resetErrorsFor('amount')
  }

  // Make sure all validations are run
  const validate = () => {
    const results = [
      beneficiarySelect.validate(),
      validation.validate({ amount }),
      tribute.validate(),
      plugins.validate(),
    ]
    return results.length == results.filter(Boolean).length
  }

  const handleSubmit = async e => {
    e.preventDefault()
    if (loading || !validate()) return

    try {
      setLoading(true)

      const { errors, token, serializedDonation } = await record.saveDraft({
        order: { payment_source: 'wallet_balance' }
      })

      if (errors) return validation.setErrors(errors)

      const response = await fetch(submitUrl, {
        method: 'POST',
        headers: csrfHeaders(),
        body: JSON.stringify({ order_id: token }),
      })

      const { balance, errors: processingErrors } = await response.json()
      if (!response.ok) return validation.setErrors(processingErrors)

      setDonation({ ...serializedDonation, token, balance, partnerItem })
      setBalance(balance)
    } catch (error) {
      console.error(error)
      validation.setErrors({
        amount: [ 'An error occured trying to make your donation' ],
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Intro i18n={i18n} />

      {plugins.renderError()}
      <InvalidForm>
        {baseErrors?.[0]}
        {unknownErrors.map((msg, i) => <div key={i}>{msg}</div>)}
      </InvalidForm>
      {plugins.renderHook('afterIntro', 'wallet')}

      <Tribute tribute={tribute} />

      <h2 className="h5 section-heading section-heading-donation">
        {i18n.t('donation')}
      </h2>

      {beneficiarySelect.render()}

      <InputField
        {...commonInputProps(i18n.t('amount'))}
        type={SandwichField}
        forwardType={MoneyField}
        id="amount"
        errors={validation.errors}
        className="form-control form-control-lg money-field"
        prefix=" $ "
        value={amount}
        onChange={handleAmountChange}
        required
      />

      <p className="bg-light px-3 py-2 rounded-3">
        {balanceLabel}: <b>{formatUsdAmount(balance)}</b>
      </p>

      {plugins.renderHook('afterAmounts', 'wallet')}
      {plugins.renderHook('afterSubscription', 'wallet')}

      <div className="mb-3">
        <DataSharing
          i18n={i18n}
          foundation={foundation}
          partner={partner}
          partner_is_pledge={partner_is_pledge}
          data_sharing={data_sharing}
          beneficiary={partnerItem.beneficiary}
          defaultShares={defaultShares}
          shares={shares}
          setShares={setShares}
        />
      </div>

      <div className="d-grid">
        <button className="btn btn-lg btn-block btn-primary" disabled={loading}>
          {loading ? 'Donating...' : 'Donate'}
        </button>
      </div>
    </form>
  )
}

export default App
